import { apiConfig } from '../config/api';
import { ChatMessage, ChatResponse, ChatHistory, ChatAttachment } from '../types';

class ChatService {
  async initiateChat(
    deckId: string,
    message: string,
    attachments: ChatAttachment[],
    token: string,
    slideNumber?: number | string
  ): Promise<{ requestId: string; status: string }> {
    // Build request body, only include slideNumber if it's a valid number
    const requestBody: any = {
      message,
      attachments,
    };
    
    // Convert slideNumber to number if it's a string, then validate
    let numericSlideNumber: number | undefined;
    if (slideNumber !== undefined && slideNumber !== null) {
      numericSlideNumber = typeof slideNumber === 'string' ? parseInt(slideNumber, 10) : slideNumber;
      if (!isNaN(numericSlideNumber)) {
        requestBody.slideNumber = numericSlideNumber;
      }
    }
    
    const response = await fetch(`${apiConfig.baseUrl}/api/chat/${deckId}/initiate`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      let errorMessage = 'Failed to initiate chat';
      try {
        const errorData = await response.json();
        errorMessage = errorData.error || errorData.message || errorMessage;
      } catch (e) {
        errorMessage = `${errorMessage}: ${response.statusText}`;
      }
      throw new Error(errorMessage);
    }

    const data = await response.json();
    return data.data;
  }

  async getChatResponse(
    requestId: string,
    token: string
  ): Promise<{
    status: 'processing' | 'completed' | 'error';
    requestId: string;
    content?: string;
    messageId?: string;
    timestamp?: string;
    errorMessage?: string;
    isPartial?: boolean;
  }> {
    const response = await fetch(`${apiConfig.baseUrl}/api/chat/response/${requestId}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      let errorMessage = 'Failed to get chat response';
      try {
        const errorData = await response.json();
        errorMessage = errorData.error || errorData.message || errorMessage;
      } catch (e) {
        errorMessage = `${errorMessage}: ${response.statusText}`;
      }
      throw new Error(errorMessage);
    }

    const data = await response.json();
    return data.data;
  }

  // Keep old sendMessage for backward compatibility (deprecated)
  async sendMessage(
    deckId: string,
    message: string,
    attachments: ChatAttachment[],
    token: string,
    slideNumber?: number
  ): Promise<ChatResponse> {
    const response = await fetch(`${apiConfig.baseUrl}${apiConfig.endpoints.chat(deckId)}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({
        message,
        attachments,
        slideNumber,
      }),
    });

    if (!response.ok) {
      let errorMessage = 'Failed to send message';
      try {
        const errorData = await response.json();
        errorMessage = errorData.error || errorData.message || errorMessage;
      } catch (e) {
        // If response body is not JSON, use status text
        errorMessage = `${errorMessage}: ${response.statusText}`;
      }
      throw new Error(errorMessage);
    }

    const data = await response.json();
    return data.data;
  }

  async getChatHistory(
    deckId: string,
    token: string,
    limit: number = 50,
    before?: string,
    slideNumber?: number | string
  ): Promise<ChatHistory> {
    const params = new URLSearchParams();
    params.append('limit', limit.toString());
    if (before) params.append('before', before);
    
    // Convert slideNumber to number if it's a string, then validate and add to params
    if (slideNumber !== undefined && slideNumber !== null) {
      const numericSlideNumber = typeof slideNumber === 'string' ? parseInt(slideNumber, 10) : slideNumber;
      if (!isNaN(numericSlideNumber)) {
        params.append('slideNumber', numericSlideNumber.toString());
      }
    }

    const url = `${apiConfig.baseUrl}${apiConfig.endpoints.chatHistory(deckId)}?${params}`;
    const response = await fetch(url, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      let errorMessage = 'Failed to fetch chat history';
      try {
        const errorData = await response.json();
        errorMessage = errorData.error || errorData.message || errorMessage;
      } catch (e) {
        // If response body is not JSON, use status text
        errorMessage = `${errorMessage}: ${response.statusText}`;
      }
      throw new Error(errorMessage);
    }

    const data = await response.json();
    return data.data;
  }

  async requestReanalysis(
    deckId: string,
    slides: number[],
    tasks: string[],
    token: string
  ): Promise<any> {
    const response = await fetch(
      `${apiConfig.baseUrl}${apiConfig.endpoints.reanalyze(deckId)}`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          slides,
          tasks,
          reason: 'User requested via chat',
        }),
      }
    );

    if (!response.ok) {
      throw new Error('Failed to request reanalysis');
    }

    const data = await response.json();
    return data.data;
  }

  async getAttachmentPresignedUrl(
    deckId: string,
    fileName: string,
    fileType: string,
    fileSize: number,
    token: string
  ): Promise<{ uploadUrl: string; s3Key: string; expiresAt: string }> {
    const response = await fetch(
      `${apiConfig.baseUrl}${apiConfig.endpoints.attachmentPresign(deckId)}`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          fileName,
          fileType,
          fileSize,
        }),
      }
    );

    if (!response.ok) {
      throw new Error('Failed to get presigned URL');
    }

    const data = await response.json();
    return data.data;
  }

  async uploadAttachment(
    uploadUrl: string,
    file: File,
    onProgress?: (progress: number) => void
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();

      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable && onProgress) {
          const progress = (e.loaded / e.total) * 100;
          onProgress(progress);
        }
      });

      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve();
        } else {
          reject(new Error(`Upload failed with status ${xhr.status}`));
        }
      });

      xhr.addEventListener('error', () => {
        reject(new Error('Upload failed'));
      });

      xhr.open('PUT', uploadUrl);
      xhr.setRequestHeader('Content-Type', file.type);
      xhr.send(file);
    });
  }
}

export const chatService = new ChatService();
export default ChatService;

